import React from "react";
import { Compass, Flame, Snowflake, SunMedium } from "lucide-react";
import { formatPercent, formatCurrency } from "./dashboardUtils";
import type { Deal } from "../types";

interface LeadSourceAndTemperatureCardProps {
  deals: Deal[];
  currency?: string;
}

export const LeadSourceAndTemperatureCard: React.FC<
  LeadSourceAndTemperatureCardProps
> = ({ deals, currency = "BRL" }) => {
  // 1. Agrupamento por Origem
  const sourceStats = React.useMemo(() => {
    const counts: Record<string, number> = {};
    deals.forEach((d) => {
      let src = (d.origem || "").trim();
      if (!src) src = "Não Informado";
      else {
        // Normaliza nomes conhecidos
        const lower = src.toLowerCase();
        if (lower.includes("indica")) src = "Indicação";
        else if (lower.includes("goog")) src = "Google";
        else if (lower.includes("insta")) src = "Instagram";
        else if (lower.includes("link")) src = "LinkedIn";
        else if (lower.includes("outbound") || lower.includes("ativo"))
          src = "Outbound";
        else if (lower.includes("site") || lower.includes("web"))
          src = "Website";
        else if (lower.includes("whats")) src = "WhatsApp";
        else src = src.charAt(0).toUpperCase() + src.slice(1);
      }
      counts[src] = (counts[src] || 0) + 1;
    });

    return Object.entries(counts)
      .map(([name, count]) => ({
        name,
        count,
        percent: deals.length > 0 ? (count / deals.length) * 100 : 0,
      }))
      .sort((a, b) => b.count - a.count);
  }, [deals]);

  // 2. Agrupamento por Temperatura
  const temperatureStats = React.useMemo(() => {
    let frio = 0;
    let morno = 0;
    let quente = 0;
    let naoDefinido = 0;

    let frioAmount = 0;
    let mornoAmount = 0;
    let quenteAmount = 0;

    deals.forEach((d) => {
      const temp = (d.temperatura || "").toLowerCase().trim();
      const amount = Number(d.amount) || 0;

      if (temp === "quente") {
        quente += 1;
        quenteAmount += amount;
      } else if (temp === "morno") {
        morno += 1;
        mornoAmount += amount;
      } else if (temp === "frio") {
        frio += 1;
        frioAmount += amount;
      } else {
        naoDefinido += 1;
      }
    });

    const total = deals.length || 1;

    return {
      quente: {
        count: quente,
        percent: (quente / total) * 100,
        amount: quenteAmount,
      },
      morno: {
        count: morno,
        percent: (morno / total) * 100,
        amount: mornoAmount,
      },
      frio: { count: frio, percent: (frio / total) * 100, amount: frioAmount },
      naoDefinido: { count: naoDefinido, percent: (naoDefinido / total) * 100 },
    };
  }, [deals]);

  const sourceColors = [
    "bg-indigo-500",
    "bg-emerald-500",
    "bg-blue-500",
    "bg-amber-500",
    "bg-purple-500",
    "bg-rose-500",
    "bg-cyan-500",
  ];

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
      {/* Por Origem */}
      <div className="bg-card border border-border/80 rounded-xl p-4 shadow-xs">
        <div className="flex items-center justify-between border-b border-border/50 pb-3">
          <div className="flex items-center gap-2">
            <div className="w-7 h-7 rounded-lg bg-primary/10 text-primary flex items-center justify-center">
              <Compass className="w-3.5 h-3.5" />
            </div>
            <div>
              <h3 className="text-sm font-semibold text-foreground">
                Por Origem
              </h3>
              <p className="text-[11px] text-muted-foreground">
                Quantidade de leads agrupada por canal de entrada
              </p>
            </div>
          </div>
          <span className="text-xs font-semibold px-2 py-0.5 rounded-full bg-primary/10 text-primary">
            {sourceStats.length} origens
          </span>
        </div>

        <div className="mt-4 space-y-3">
          {sourceStats.length === 0 ? (
            <div className="text-center py-6 text-xs text-muted-foreground">
              Nenhuma origem registrada
            </div>
          ) : (
            sourceStats.slice(0, 6).map((src, idx) => (
              <div key={src.name} className="space-y-1">
                <div className="flex items-center justify-between text-xs">
                  <span className="font-medium text-foreground">
                    {src.name}
                  </span>
                  <span className="text-muted-foreground font-semibold">
                    {src.count} ({formatPercent(src.percent)})
                  </span>
                </div>
                <div className="w-full h-2 rounded-full bg-muted overflow-hidden">
                  <div
                    className={`h-full rounded-full transition-all duration-500 ${
                      sourceColors[idx % sourceColors.length]
                    }`}
                    style={{ width: `${src.percent}%` }}
                  />
                </div>
              </div>
            ))
          )}
        </div>
      </div>

      {/* Leads por Temperatura */}
      <div className="bg-card border border-border/80 rounded-xl p-4 shadow-xs">
        <div className="flex items-center justify-between border-b border-border/50 pb-3">
          <div className="flex items-center gap-2">
            <div className="w-7 h-7 rounded-lg bg-amber-500/10 text-amber-500 flex items-center justify-center">
              <Flame className="w-3.5 h-3.5" />
            </div>
            <div>
              <h3 className="text-sm font-semibold text-foreground">
                Leads por Temperatura
              </h3>
              <p className="text-[11px] text-muted-foreground">
                Maturação e probabilidade de fechamento dos negócios
              </p>
            </div>
          </div>
          <span className="text-xs font-semibold px-2 py-0.5 rounded-full bg-amber-500/10 text-amber-600 dark:text-amber-400">
            {deals.length} leads
          </span>
        </div>

        <div className="mt-4 grid grid-cols-1 gap-3">
          {/* Quente */}
          <div className="flex items-center justify-between p-2.5 rounded-lg border border-red-500/20 bg-red-500/5">
            <div className="flex items-center gap-2.5">
              <div className="w-7 h-7 rounded-full bg-red-500/15 text-red-600 dark:text-red-400 flex items-center justify-center">
                <Flame className="w-4 h-4" />
              </div>
              <div>
                <span className="text-xs font-bold text-red-600 dark:text-red-400 block">
                  Quente (Alta Probabilidade)
                </span>
                <span className="text-[10px] text-muted-foreground">
                  Negociação avançada ou fechamento iminente
                </span>
              </div>
            </div>
            <div className="text-right">
              <span className="text-xs font-bold text-red-600 dark:text-red-400">
                {temperatureStats.quente.count} leads
              </span>
              <span className="text-[10px] text-muted-foreground block">
                {formatCurrency(temperatureStats.quente.amount, currency)}
              </span>
            </div>
          </div>

          {/* Morno */}
          <div className="flex items-center justify-between p-2.5 rounded-lg border border-amber-500/20 bg-amber-500/5">
            <div className="flex items-center gap-2.5">
              <div className="w-7 h-7 rounded-full bg-amber-500/15 text-amber-600 dark:text-amber-400 flex items-center justify-center">
                <SunMedium className="w-4 h-4" />
              </div>
              <div>
                <span className="text-xs font-bold text-amber-600 dark:text-amber-400 block">
                  Morno (Em Qualificação)
                </span>
                <span className="text-[10px] text-muted-foreground">
                  Contato ativo e proposta em elaboração
                </span>
              </div>
            </div>
            <div className="text-right">
              <span className="text-xs font-bold text-amber-600 dark:text-amber-400">
                {temperatureStats.morno.count} leads
              </span>
              <span className="text-[10px] text-muted-foreground block">
                {formatCurrency(temperatureStats.morno.amount, currency)}
              </span>
            </div>
          </div>

          {/* Frio */}
          <div className="flex items-center justify-between p-2.5 rounded-lg border border-blue-500/20 bg-blue-500/5">
            <div className="flex items-center gap-2.5">
              <div className="w-7 h-7 rounded-full bg-blue-500/15 text-blue-600 dark:text-blue-400 flex items-center justify-center">
                <Snowflake className="w-4 h-4" />
              </div>
              <div>
                <span className="text-xs font-bold text-blue-600 dark:text-blue-400 block">
                  Frio (Início do Funil)
                </span>
                <span className="text-[10px] text-muted-foreground">
                  Primeiros contatos ou nutrição de lead
                </span>
              </div>
            </div>
            <div className="text-right">
              <span className="text-xs font-bold text-blue-600 dark:text-blue-400">
                {temperatureStats.frio.count} leads
              </span>
              <span className="text-[10px] text-muted-foreground block">
                {formatCurrency(temperatureStats.frio.amount, currency)}
              </span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
